import { FC, useMemo } from "react";

import { EventTypeIcon } from "Bands/components";
import { IEvent, IIconBandSetting } from "Bands/types";
import { getDaysLeft, useBandRegionalSettings } from "Bands/utils";
import { copy } from "shared/assets";
import { Menu } from "shared/assets/icons";
import { ActionDropdown, IActionDropdownItem, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { BoldText, MutedText } from "shared/styles";
import { colors, fontSize } from "shared/styles/constants";
import { formatDateTime, formatTime, TimeFormat } from "shared/utils";
import { StyledEventHeader, StyledTillEvent } from "./styles";

const { placeholder } = copy;
const eventProperties = copy.event.properties;

interface EventHeaderProps {
  event: Partial<IEvent>;
  actions?: IActionDropdownItem[];
  eventTypes?: IIconBandSetting[];
  titleSuffix?: string;
  showActions?: boolean;
}

export const EventHeader: FC<EventHeaderProps> = ({
  event: { title, date, type, time, status, description },
  eventTypes,
  actions,
  titleSuffix,
  showActions = false,
}) => {
  const { timeFormat } = useBandRegionalSettings();

  const daysTillEventCopy = useMemo(() => {
    if (date) {
      const daysLeft = getDaysLeft(date);
      if (daysLeft && daysLeft > 0) {
        return `${daysLeft} ${
          daysLeft > 1
            ? eventProperties.daysTillEvent.plural
            : eventProperties.daysTillEvent.singular
        }`;
      }
    }
  }, [date]);

  const actionSelectProps = useMemo(
    () => ({
      label: copy.menu.open,
      options: actions || [],
      triggerIcon: Menu,
    }),
    [actions]
  );

  return (
    <StyledEventHeader size="m">
      <Spacer
        spaceDirection="horizontal"
        size="l"
        justifyContent="space-between"
        align="flex-start"
        flexWrap="nowrap"
      >
        <Spacer
          size="l"
          spaceDirection="horizontal"
          align="center"
          flexWrap="nowrap"
          mobile={{ align: "flex-start" }}
        >
          {type && status && <EventTypeIcon type={type} status={status} eventTypes={eventTypes} />}
          <Spacer size="xs">
            <Title
              asComponent="h3"
              printOptions={{
                color: colors.black,
                fontSize: fontSize.huge,
              }}
            >
              {title || placeholder.eventTitle}{" "}
              {titleSuffix && <MutedText>({titleSuffix})</MutedText>}
            </Title>
            <Spacer
              size="s"
              spaceDirection="horizontal"
              mobile={{ spaceDirection: "vertical", size: "xxs" }}
            >
              <BoldText>
                <MutedText>
                  {date
                    ? formatDateTime(date, { dateOnly: true, longForm: true })
                    : placeholder.date}{" "}
                  {time
                    ? `at ${formatTime({
                        time,
                        timeFormat: timeFormat || TimeFormat.twelve,
                      })}`
                    : ""}
                </MutedText>
              </BoldText>
              {!!daysTillEventCopy && <StyledTillEvent>{daysTillEventCopy}</StyledTillEvent>}
            </Spacer>
          </Spacer>
        </Spacer>
        {!!actions?.length && showActions && (
          <ActionDropdown
            {...actionSelectProps}
            placement="bottom-end"
            dataTestId="event_actions"
          />
        )}
      </Spacer>

      {description && <MutedText dangerouslySetInnerHTML={{ __html: description || "" }} />}
    </StyledEventHeader>
  );
};
